/**
 * The shell handshake — the other half of apps/web's `GameMount`.
 *
 * INTEGRATION NOTE: this file did not exist when the client was first written.
 * `config.js` read the session token out of `?token=`, while the shell had
 * already decided — correctly — that credentials never travel in a URL, because
 * a match URL gets shared, screenshotted, and written to history and referrer
 * headers. Nothing connected the two, so the shell would have sat on "Entering
 * the arena" forever and the game would have joined with no token.
 *
 * The contract, verbatim from apps/web/src/components/match/GameMount.tsx:
 *
 *   game  -> shell   { type: "chaos:ready" }            iframe has booted
 *   shell -> game    { type: "chaos:session", matchId, roomId, realtimeUrl,
 *                      sessionToken }
 *   game  -> shell   { type: "chaos:ended", matchId }   the match is over
 *
 * Two acceptance rules, both structural rather than advisory:
 *   - `event.source` must be `window.parent`. Only the real embedder can be
 *     that object; a same-origin popup or a sibling frame cannot forge it.
 *   - when we can name the shell's origin (VITE_SHELL_ORIGIN, else the
 *     referrer's origin) `event.origin` must equal it.
 *
 * STANDALONE DEV still works: with no parent frame we fall back to URL params,
 * which is the only place a `?token=` is acceptable — a developer's own tab.
 */

const READY = "chaos:ready";
const SESSION = "chaos:session";
const ENDED = "chaos:ended";

/** How long we wait for the shell before falling back to URL params. */
const HANDSHAKE_TIMEOUT_MS = 8000;

const isEmbedded = typeof window !== "undefined" && window.parent !== window;

function expectedShellOrigin() {
  const configured = import.meta.env?.VITE_SHELL_ORIGIN;
  if (configured) {
    try {
      return new URL(configured).origin;
    } catch {
      /* fall through to the referrer */
    }
  }
  if (document.referrer) {
    try {
      return new URL(document.referrer).origin;
    } catch {
      return null;
    }
  }
  // Referrer suppressed by policy. `event.source === window.parent` is still
  // enforced below, so this degrades to a weaker check rather than none.
  return null;
}

const shellOrigin = isEmbedded ? expectedShellOrigin() : null;

/** Where `chaos:ended` is posted. Never "*" — that would leak the match id. */
let replyOrigin = shellOrigin;

function readUrlSession() {
  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get("token");
  if (fromUrl) {
    try {
      sessionStorage.setItem("chaos.token", fromUrl);
    } catch {
      /* private mode — the URL copy still works for this load */
    }
  }
  let stored = null;
  try {
    stored = sessionStorage.getItem("chaos.token");
  } catch {
    /* ignore */
  }
  return {
    matchId: params.get("match") || "",
    roomId: params.get("room") || "",
    realtimeUrl: params.get("server") || "",
    sessionToken: fromUrl || stored || "",
  };
}

function accepts(event) {
  if (event.source !== window.parent) return false;
  if (shellOrigin !== null && event.origin !== shellOrigin) return false;
  const data = event.data;
  return Boolean(data) && data.type === SESSION && typeof data.sessionToken === "string";
}

let resolved = null;

/**
 * Resolves with `{ matchId, roomId, realtimeUrl, sessionToken }`.
 *
 * Never rejects: an empty token is returned instead, and `Connection.join`
 * turns that into one clear boot error rather than a hang.
 */
export function awaitShellSession() {
  if (resolved) return resolved;

  if (!isEmbedded) {
    resolved = Promise.resolve(readUrlSession());
    return resolved;
  }

  resolved = new Promise((resolve) => {
    let done = false;
    const finish = (value) => {
      if (done) return;
      done = true;
      window.removeEventListener("message", onMessage);
      clearTimeout(timer);
      resolve(value);
    };

    const onMessage = (event) => {
      if (!accepts(event)) return;
      // Pin replies to the origin that actually spoke to us.
      replyOrigin = event.origin;
      finish({
        matchId: typeof event.data.matchId === "string" ? event.data.matchId : "",
        roomId: typeof event.data.roomId === "string" ? event.data.roomId : "",
        realtimeUrl: typeof event.data.realtimeUrl === "string" ? event.data.realtimeUrl : "",
        sessionToken: event.data.sessionToken,
      });
    };

    const timer = setTimeout(() => {
      // The shell never answered. URL params are all that is left; if they are
      // empty too, join() fails loudly, which is the correct end state.
      console.warn("[shell] no chaos:session within %dms — falling back to URL", HANDSHAKE_TIMEOUT_MS);
      finish(readUrlSession());
    }, HANDSHAKE_TIMEOUT_MS);

    window.addEventListener("message", onMessage);

    // Announce only after the listener is attached, or a shell that replies
    // synchronously would be talking to nobody.
    window.parent.postMessage({ type: READY }, shellOrigin ?? "*");
  });

  return resolved;
}

/**
 * Tell the shell the match is over. A NAVIGATION HINT ONLY — the shell re-fetches
 * the result from the server, so this cannot invent a placement or a payout.
 */
export function postMatchEnded(matchId) {
  if (!isEmbedded) return;
  try {
    window.parent.postMessage({ type: ENDED, matchId: matchId ?? "" }, replyOrigin ?? "*");
  } catch {
    /* the shell went away; nothing here can fix that */
  }
}
